import React, { useCallback, useContext, useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import { FeatureIdentifier } from "mapbox-gl";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import { LayerIds } from "fond/layers";
import { MapContext } from "fond/map/MapProvider";
import { lineStringDrawingConfig } from "fond/map/Toolbar";
import { useAppSelector } from "fond/utils/hooks";

import directSelectLeadIns from "./directSelectLeadIns";
import drawLeadIns from "./drawLeadIns";
import { isValid, isValidTree } from "./helper";
import leadInDrawStyles from "./leadInDrawStyles";
import simpleSelectLeadIns from "./simpleSelectLeadIns";

const modes = {
  simple_select: simpleSelectLeadIns,
  direct_select: directSelectLeadIns,
  draw_line_string: drawLeadIns,
  passing_draw_polygon: passingDrawMode,
};

const sources: FeatureIdentifier[] = [{ source: LayerIds.inLeadIns }];

/**
 * Component that handles the drawing of the Lead In layer features within FOND Planner Extended.
 */
const LeadInHandler: React.FC = () => {
  const { map } = useContext(MapContext);
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  // The initial features are derived from the data layer for lead-ins which
  // are drawn as LineStrings.
  const initialFeatures: Feature<LineString>[] = useMemo(() => {
    const leadIns = (data?.layers[LayerIds.inLeadIns].features || []) as Feature<LineString>[];

    // We validate each lead-in based on whether it is connected to both a demand point & an underground point.
    // We use a spatial index to make this check more efficient.
    const tree = rbush();
    tree.load([
      ...((data?.layers[LayerIds.inAddress].features || []) as Feature<LineString>[]),
      ...((data?.layers[LayerIds.inStreetPoints].features || []) as Feature<Point>[]),
    ]);

    return leadIns.map((f) => {
      if (f.properties) {
        f.properties[`${DRAWING_CONSTANTS.Valid}`] = String(isValidTree(f, tree));
      }
      return f;
    });
  }, [data]);

  /**
   * Determines the features that the item being dragged can snap to.
   */
  const snapGetFeatures = useCallback(
    (m: mapboxgl.Map) => [LayerIds.inStreetPoints, LayerIds.inAddress].map((layerId) => m.querySourceFeatures(layerId)).flat(),
    []
  );

  /**
   * Custom snapping options — only snapGetFeatures is handler-specific;
   * user-facing snap values (snapPx, etc.) come from persisted preferences.
   */
  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        snap: true,
        snapOptions: {
          snapGetFeatures,
        },
      }) as MapboxDrawOptions,
    [snapGetFeatures]
  );

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback(
    (feature: Feature<LineString>) => ({ layerId: LayerIds.inLeadIns, [DRAWING_CONSTANTS.Valid]: map && isValid(feature, map).toString() }),
    [map]
  );

  return (
    <DrawHandler
      startingMode={initialFeatures.length > 0 ? "simple_select" : "draw_line_string"}
      initialFeatures={initialFeatures}
      sources={sources}
      styles={leadInDrawStyles}
      config={lineStringDrawingConfig}
      getFeatureProperties={getFeatureProperties}
      options={options}
      modes={modes}
    />
  );
};

export default LeadInHandler;
